import React from "react";
import PropTypes from "prop-types";

import "./app-download-hub.scss";

import SVGWaFdBankTreasuryQR from "../../images/wafd-treasury-app-qr.svg";
import SVGWaFdBankAppQR from "../../images/wafd-mobile-app-qr.svg";

import MobileAppDownloadBtn from "./mobile-app-download-btn";
import MobileAppQR from "./mobile-app-qr";
import { useLanguageContext } from "../../contexts/language-context";

/**
 * @typedef {Object} AppBtnData
 * @property {string} containerClass - classes applied to the div container of the button and text below the button.
 * @property {string} id - id used for the anchor element.
 * @property {string} url - store url.
 * @property {string} storeBtn - something like "apple" and "google", we could add more stores.
 * @property {Object} anchorStyle - extra styles applied to the anchor element containing the image.
 * @property {Object} containerStyle - extra styles applied to the div containing the text and anchor.
 * @property {Object} imgStyle - extra styles applied to the app store image inside the anchor link.
 * @property {string} textClass - classes applied to the `<p>` that shows the text below the button.
 * @property {string} text - `<p>` that shows the text below the button.
 * @property {string} br - border radius applied to the image/icon of the button.
 * @property {string} mt - custom margin top for the image/icon of the button.
 * @property {string} mb - custom margin bottom for the image/icon of the button.
 */

/**
 * @typedef {Object} AppQrData
 * @property {string} src - src for the image or key to find the svg in the QR_SVG constant in this file.
 * @property {string} altText - alt text for the image.
 * @property {Object} containerStyle - extra styles applied to the div containing the text and image.
 * @property {Object} style - custom style to force some visual requirements.
 * @property {string} textClass - text classes applied to the text below the image.
 * @property {string} text - you can change the text below the image here.
 */

/**
 * This renders the mobile app download button with its description text.
 *
 * @param {Object} props - The object containing props.
 * @param {string} props.id - id for the div container.
 * @param {string} props.containerClass - classes applied to the div container.
 * @param {Object} props.style - custom style to force some visual requirements.
 * @param {string} props.structureStyle - extra classes to handle the structure behavior, you can combine more than one at the same time and add extra classes and it will be applied to the div container example: "qr-left", "qr-hidden", "qr-above", "qr-below", "buttons-row", "buttons-column", "show-qr-mobile".
 * @param {Object} props.appButtonsContainerStyle - custom styles for the div container of the buttons for the available app stores.
 * @param {AppBtnData} props.mainBtn - by default is the google play store button, we can add the url and more props.
 * @param {AppBtnData} props.secondaryBtn - by default is the apple store button, we can add the url and more props.
 * @param {AppQrData} props.qrImage - object with the data to use the proper qr image.
 * @returns {React.JSX.Element} Returns the JSX component to render
 */
const AppDownloadHub = ({
  id = "app-download-hub-container",
  containerClass = "col-12 col-md-7 col-xl-6",
  style = {},
  structureStyle = "",
  appButtonsContainerStyle = {},
  mainBtn = null,
  secondaryBtn = null,
  qrImage = null
}) => {
  const isSpanish = useLanguageContext();

  const DEFAULT_MAIN_BUTTON_PROPS = {
    containerClass: "",
    id: "google-play-store",
    url: "https://play.google.com/store/apps/details?id=com.washingtonfederal.mobilebanking&hl=en_US",
    storeBtn: "google",
    altText: isSpanish
      ? "Descarga la app móvil WaFd Bank en Google Play Store."
      : "Download WaFd Bank mobile app in Google Play Store.",
    textClass: "text-gray-60 text-sm text-center",
    text: isSpanish ? "Dispositivos android" : "For Android Devices"
  };

  const DEFAULT_SECONDARY_BUTTON_PROPS = {
    containerClass: "",
    id: "apple-store",
    url: "https://apps.apple.com/us/app/wafd-bank/id1054549841",
    storeBtn: "apple",
    altText: isSpanish
      ? "Descarga la app móvil WaFd Bank en Apple App Store."
      : "Download WaFd Bank mobile app in Apple App Store.",
    textClass: "text-gray-60 text-sm text-center",
    text: isSpanish ? "Dispositivos apple" : "For Apple Devices"
  };

  const DEFAULT_QR_PROPS = {
    src: SVGWaFdBankAppQR,
    altText: isSpanish ? "Código QR de app WaFd." : "WaFd App QR Code.",
    textClass: "text-gray-60 text-sm",
    text: isSpanish ? "Escanear para descargar" : "Scan to Download"
  };

  const mainBtnProps = mainBtn ? { ...DEFAULT_MAIN_BUTTON_PROPS, ...mainBtn } : DEFAULT_MAIN_BUTTON_PROPS;

  const secondaryBtnProps = secondaryBtn
    ? { ...DEFAULT_SECONDARY_BUTTON_PROPS, ...secondaryBtn }
    : DEFAULT_SECONDARY_BUTTON_PROPS;

  const QR_SVG = {
    "svg-wafd-bank-treasury-app": SVGWaFdBankTreasuryQR,
    "svg-wafd-mobile-app": SVGWaFdBankAppQR
  };

  const qrPreProps = qrImage ? { ...DEFAULT_QR_PROPS, ...qrImage } : DEFAULT_QR_PROPS;

  const qrProps = qrPreProps.src.includes("svg")
    ? { ...qrPreProps, src: QR_SVG[qrPreProps.src] || SVGWaFdBankAppQR }
    : qrPreProps;

  return (
    <div className={"container row " + containerClass}>
      <div id={id} className={"app-download-hub-elements-container " + structureStyle} style={style}>
        <div className={"app-download-hub-buttons-container"} style={appButtonsContainerStyle}>
          <MobileAppDownloadBtn {...mainBtnProps} />

          <MobileAppDownloadBtn {...secondaryBtnProps} />
        </div>

        {!structureStyle.includes("qr-hidden") && <MobileAppQR {...qrProps} />}
      </div>
    </div>
  );
};

export default AppDownloadHub;

AppDownloadHub.propTypes = {
  id: PropTypes.string,
  containerClass: PropTypes.string,
  style: PropTypes.object,
  structureStyle: PropTypes.string,
  appButtonsContainerStyle: PropTypes.object,
  mainBtn: PropTypes.shape({
    containerClass: PropTypes.string,
    id: PropTypes.string,
    url: PropTypes.string,
    storeBtn: PropTypes.string,
    textClass: PropTypes.string,
    text: PropTypes.string
  }),
  secondaryBtn: PropTypes.shape({
    containerClass: PropTypes.string,
    id: PropTypes.string,
    url: PropTypes.string,
    storeBtn: PropTypes.string,
    textClass: PropTypes.string,
    text: PropTypes.string
  }),
  qrImage: PropTypes.shape({
    src: PropTypes.string,
    altText: PropTypes.string,
    textClass: PropTypes.string,
    text: PropTypes.string,
    style: PropTypes.object
  })
};
